import { isNotFunction } from "./Function";
import { isNullish } from "./Null";

export function hasElements<T>(list: T[]): boolean {
  if (!Array.isArray(list)) {
    return false;
  }

  return list.length > 0;
}

export function hasNoElements<T>(list: T[]): boolean {
  return !hasElements(list);
}

export function hasElement<T>(list: T[], condition: (element: T) => boolean): boolean {
  if (list == null) {
    return false;
  }

  return list.findIndex((entry) => condition(entry)) > -1;
}

export function hasNoElement<T>(list: T[], condition: (element: T) => boolean): boolean {
  return !hasElement(list, condition);
}

export function isIndexInRange<T>(list: T[], index: number): boolean {
  if (!Array.isArray(list)) {
    return false;
  }

  return index >= 0 && index < list.length;
}

export function isIndexNotInRange<T>(list: T[], index: number): boolean {
  return !isIndexInRange(list, index);
}

export function removeElementAt<T>(list: T[], index: number): T[] {
  if (hasNoElements(list)) {
    return list;
  }

  if (index < 0) {
    return list;
  }

  const clonedList = fastClone(list);
  clonedList.splice(index, 1);
  return clonedList;
}

export function removeElement<T>(list: T[], element: T): T[] {
  if (hasNoElements(list)) {
    return list;
  }

  const index = list.findIndex((value) => value === element);
  return removeElementAt(list, index);
}

export function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values ?? []));
}

export function toSafeArray<T>(values: T | T[]): T[] {
  if (values == null) {
    return [];
  }
  return Array.isArray(values) ? values : [values];
}

export function safeUnique<T>(values: T | T[]): T[] {
  return unique(toSafeArray(values));
}

export function nonNull<T>(values: T[]): T[] {
  return values.filter((value) => value != null);
}

export function firstOrDefault<T>(list: T[]): T {
  if (hasNoElements(list)) {
    return null;
  }

  return list[0];
}

export function lastOrDefault<T>(list: T[]): T {
  if (hasNoElements(list)) {
    return null;
  }

  return list[list.length - 1];
}

export function omitNullish<T>(list: T[]): T[] {
  if (hasNoElements(list)) {
    return list;
  }

  return list.filter((entry) => !isNullish(entry));
}

export function omitByFn<T>(list: T[], fn: (value: T) => boolean): T[] {
  if (hasNoElements(list) || isNotFunction(fn)) {
    return list;
  }

  return list.filter((value) => !fn(value));
}

export function upsert2<T>(list: T[], value: T, key: string): T[] {
  type Upsert = [unknown, T];
  const mapEntries = [...list.map<Upsert>((entry) => [entry[key], entry]), [value[key], value] as Upsert];
  return Array.from(new Map<unknown, T>(mapEntries).values());
}

export function upsert<T>(list: T[], value: T, key: string): T[] {
  for (let i = 0; i < list.length; i++) {
    if (list[i][key] === value[key]) {
      const clonedList = fastClone(list);
      clonedList[i] = value;
      return clonedList;
    }
  }

  return list.concat([value]);
}

export function intersect<T>(listA: T[], listB: T[]): T[] {
  if (hasNoElements(listA) || hasNoElements(listB)) {
    return [];
  }

  return listA.filter((a) => listB.indexOf(a) > -1);
}

export function fastClone<T>(list: T[]): T[] {
  return list?.slice(0) ?? [];
}

export function flattenArray<T>(input: T | T[]): T[] {
  if (input == null) {
    return [];
  }

  if (!Array.isArray(input)) {
    return [input];
  }

  if (!hasElements(input)) {
    return [];
  }

  return Array.isArray(input[0]) ? (input.flat() as T[]) : (input as T[]);
}
